import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useO5AwsDeployerV1DeploymentQueryServiceDeploymentGet } from '@interxfi/api-client/generated/hooks/o5-aws-deployer-v1-deployment-query-service-deployment-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { O5AwsDeployerV1DeploymentState } from '@interxfi/types/generated/api';
import { CFStackParametersTable } from '../../../../../../../features/o5/deployment/cf-stack-parameters-table';
import { DatabaseTable } from '../../../../../../../features/o5/deployment/database-table/database-table';
import { DeploymentStatusPill } from '../../../../../../../features/o5/deployment/deployment-status-pill';
import { O5DeploymentEntityMenu } from '../../../../../../../features/o5/deployment/o5-deployment-entity-menu';
import { O5DeploymentEventSummary } from '../../../../../../../features/o5/deployment/o5-deployment-event';
import { O5DeploymentImagesTable } from '../../../../../../../features/o5/deployment/o5-deployment-images-table';
import { getO5DeploymentStateListItems } from '../../../../../../../features/o5/deployment/o5-deployment-list-items';
import { getO5DeploymentDetailPath } from '../../../../../../../features/o5/deployment/o5-deployment-routing';
import { O5DeploymentTriggersTable } from '../../../../../../../features/o5/deployment/o5-deployment-triggers-table';
import { O5DeploymentStepsTable } from '../../../../../../../features/o5/deployment/steps-table/o5-deployment-steps-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/deployment/_deployment/$deploymentId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `o5-deployment-${params.deploymentId}`,
        label: 'Deployment',
        to: getO5DeploymentDetailPath(params.deploymentId),
        suffix: (
          <CopyButton
            content={params.deploymentId}
            label="Copy Deployment ID"
            tooltip={{ copy: params.deploymentId, copied: 'Copied Deployment ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Deployment'),
  component: O5DeploymentDetail,
});

function getListItems(deployment: O5AwsDeployerV1DeploymentState | undefined) {
  const items = getO5DeploymentStateListItems(deployment);

  return {
    left: [
      {
        id: 'spec',
        items: [
          items.data.spec.appName,
          items.data.spec.environmentName,
          items.data.spec.region,
          items.data.spec.version,
          { ...items.data.spec.source.typeName, title: 'Source' },
          ...match(items.data.spec.source)
            .with({ github: P.not(P.nullish) }, ({ github }) => [github.value.summary])
            .with({ inline: P.not(P.nullish) }, ({ inline }) => [{ ...inline.value.version, title: 'Inline Source Version' }])
            .otherwise(() => []),
          items.data.spec.cfStackName,
          items.data.spec.snsTopics,
          items.data.spec.flags.formatted,
        ],
      },
      {
        id: 'template',
        items: [items.data.spec.template.region, items.data.spec.template.bucket, items.data.spec.template.key],
      },
      ...(items.data.request
        ? [
            {
              id: 'request',
              title: 'Request',
              items: [items.data.request.replyTo, items.data.request.context],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.deploymentId,
      items.stackId,
      items.environmentId,
      items.clusterId,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const stableEmptyArray: never[] = [] as const;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load deployment', 'view this deployment');

function O5DeploymentDetail() {
  const { deploymentId } = Route.useParams();
  const request = { deploymentId } as const;
  const { data, error, isPending } = useO5AwsDeployerV1DeploymentQueryServiceDeploymentGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.deployment), [data?.deployment]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <DeploymentStatusPill isPending={showPending} size="large" status={data?.deployment?.status} />
          </HorizontalStack>
          <O5DeploymentEntityMenu deploymentId={deploymentId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5DeploymentEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Steps</Typography.Level3>

          <O5DeploymentStepsTable steps={data?.deployment?.data?.steps ?? stableEmptyArray} />
        </VerticalStack>
      </PageLayout.Section>

      {data?.deployment?.data?.spec?.parameters ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Parameters</Typography.Level3>
            <CFStackParametersTable parameters={data.deployment.data.spec.parameters} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.deployment?.data?.spec?.databases ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Databases</Typography.Level3>
            <DatabaseTable databases={data.deployment.data.spec.databases} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.deployment?.data?.spec?.images ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Images</Typography.Level3>
            <O5DeploymentImagesTable images={data.deployment.data.spec.images} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {data?.deployment?.data?.spec?.triggers ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Triggers</Typography.Level3>
            <O5DeploymentTriggersTable triggers={data.deployment.data.spec.triggers} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
